import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, Legend, ResponsiveContainer, LineChart, Line, CartesianGrid } from 'recharts';
import { BarChart3 } from 'lucide-react';
import { Sector } from 'recharts';
import { useTranslation } from 'react-i18next';

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7f50', '#a78bfa', '#f472b6', '#00C49F', '#FFBB28'];

const DashboardTraffic = () => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/traffic-timeseries/')
      .then(res => setData(res.data))
      .catch(err => setError('Failed to load traffic stats'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-center">Loading traffic stats...</div>;
  if (error) return <div className="p-8 text-center text-red-500">{error}</div>;

  // Compute stat widgets: total visits per source
  const statData = data.reduce((acc, row) => {
    if (!acc[row.source_name]) acc[row.source_name] = 0;
    acc[row.source_name] += row.visit_count;
    return acc;
  }, {});

  // Prepare data for line chart: one line per source, x = date
  // Build a list of all dates (sorted)
  const allDates = Array.from(new Set(data.map(row => row.date))).sort();
  const allSources = Array.from(new Set(data.map(row => row.source_name)));
  // Build chartData: [{date, source1: count, source2: count, ...}]
  const chartData = allDates.map(date => {
    const row = { date };
    allSources.forEach(src => {
      const found = data.find(d => d.date === date && d.source_name === src);
      row[src] = found ? found.visit_count : 0;
    });
    return row;
  });

  // Pie chart label rendering function for outside labels with connector lines
  const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 24;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text
        x={x}
        y={y}
        fill="#4B2996"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={15}
        fontWeight="bold"
        style={{ textShadow: '0 1px 4px #fff, 0 0 2px #fff' }}
      >
        {`${name}: ${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="p-8 min-h-screen bg-gradient-to-br from-primary-50 to-violet-50">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-3xl font-extrabold mb-8 text-center text-primary-700 tracking-tight drop-shadow-sm">{t('UTM Traffic Analytics')}</h2>
        {data.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="p-6 bg-gradient-to-r from-primary-100 to-violet-100 rounded-full mb-4 shadow-lg">
              <BarChart3 className="h-16 w-16 text-primary-400" />
            </div>
            <div className="text-lg text-gray-500 mb-2">{t('No traffic data yet')}</div>
            <div className="text-sm text-gray-400 mb-8">{t('Share your blog with UTM links to see analytics here.')}</div>
            <div className="w-full max-w-xl h-64 bg-gradient-to-r from-primary-50 to-violet-50 rounded-3xl flex items-center justify-center shadow-inner">
              <span className="text-primary-200 text-3xl">—</span>
            </div>
          </div>
        ) : (
          <>
            {/* Stat widgets */}
            <div className="flex flex-wrap gap-6 mb-10 justify-center">
              {Object.entries(statData).map(([src, count], idx) => (
                <div key={src} className="bg-gradient-to-br from-white to-primary-50 rounded-2xl shadow-md px-8 py-6 min-w-[140px] text-center border border-primary-100 hover:shadow-xl transition-all">
                  <div className="text-lg font-semibold mb-1" style={{ color: COLORS[idx % COLORS.length] }}>{src}</div>
                  <div className="text-4xl font-extrabold text-primary-700 mb-1 drop-shadow-sm">{count}</div>
                  <div className="text-xs text-gray-500 tracking-wide uppercase">{t('visits')}</div>
                </div>
              ))}
            </div>
            {/* Charts side by side on desktop, stacked on mobile */}
            <div className="flex flex-col md:flex-row gap-8 items-stretch mb-10">
              {/* Line chart card */}
              <div className="flex-1 bg-gradient-to-br from-white to-violet-50 rounded-3xl shadow-lg p-8 border border-violet-100 flex flex-col justify-center">
                <h3 className="text-xl font-bold mb-4 text-violet-700">{t('Visits per Day')}</h3>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 13 }} angle={-30} textAnchor="end" height={60} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 13 }} />
                    <Tooltip />
                    <Legend />
                    {allSources.map((src, idx) => (
                      <Line
                        key={src}
                        type="monotone"
                        dataKey={src}
                        stroke={COLORS[idx % COLORS.length]}
                        strokeWidth={3}
                        dot={{ r: 4 }}
                        activeDot={{ r: 7 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {/* Pie chart card */}
              <div className="flex-1 bg-gradient-to-br from-white to-primary-50 rounded-3xl shadow-lg p-8 border border-primary-100 flex flex-col justify-center">
                <h3 className="text-xl font-bold mb-4 text-primary-700">{t('Total Visits by Source')}</h3>
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={Object.entries(statData).map(([src, count]) => ({ name: src, value: count }))}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={110}
                      innerRadius={50}
                      labelLine
                      label={renderPieLabel}
                    >
                      {Object.entries(statData).map(([src], idx) => (
                        <Cell key={src} fill={COLORS[idx % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DashboardTraffic; 